import { CloseButton, Group, TextInput } from '@mantine/core'
import { useDebouncedCallback } from '@mantine/hooks'
import { IconSearch } from '@tabler/icons-react'
import { useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { DeviceFilter } from './DeviceFilter'
import { StatusFilter } from './StatusFilter'

export type CameraListFilterConfig = {
  device_id?: string
  status?: string
  text_search?: string
}

type CameraListFilterProps = {
  filterConfig: CameraListFilterConfig
  onFilterChange: (filterConfig: CameraListFilterConfig) => void
}

const SEARCH_DEBOUNCE_MS = 400

export const CameraListFilter = ({
  filterConfig,
  onFilterChange
}: CameraListFilterProps) => {
  const intl = useIntl()
  const [searchInputValue, setSearchInputValue] = useState(
    filterConfig.text_search || ''
  )
  const searchInputRef = useRef(filterConfig.text_search || '')

  const handleFilterChange = (
    filterKey: keyof CameraListFilterConfig,
    value: string | null
  ) => {
    onFilterChange({ ...filterConfig, [filterKey]: value || '' })
  }

  const debouncedOnSearchChange = useDebouncedCallback(
    () => handleFilterChange('text_search', searchInputRef.current),
    SEARCH_DEBOUNCE_MS
  )

  const handleSearchChange = (value: string) => {
    searchInputRef.current = value
    setSearchInputValue(value)
    debouncedOnSearchChange()
  }

  useEffect(() => {
    const externalValue = filterConfig.text_search || ''

    if (externalValue !== searchInputRef.current) {
      searchInputRef.current = externalValue
      setSearchInputValue(externalValue)
    }
  }, [filterConfig.text_search])

  return (
    <Group gap="xs">
      <TextInput
        leftSection={<IconSearch size={16} />}
        aria-label={intl.formatMessage({
          id: 'cameras.list.filter.search.placeholder'
        })}
        placeholder={intl.formatMessage({
          id: 'cameras.list.filter.search.placeholder'
        })}
        value={searchInputValue}
        w={240}
        rightSection={
          searchInputValue ? (
            <CloseButton
              size="sm"
              aria-label={intl.formatMessage({
                id: 'cameras.list.filter.search.clear'
              })}
              onClick={() => handleSearchChange('')}
            />
          ) : null
        }
        onChange={(e) => handleSearchChange(e.currentTarget.value)}
      />

      <DeviceFilter
        value={filterConfig.device_id || null}
        onChange={(value) => handleFilterChange('device_id', value)}
      />

      <StatusFilter
        value={filterConfig.status || null}
        onChange={(value) => handleFilterChange('status', value)}
      />
    </Group>
  )
}
